import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faCalendarAlt, faExclamationTriangle, 
  faCheck, faClock, faInfoCircle 
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { updateDeadline } from '../../services/submissionApi';

interface DeadlineUpdateModalProps {
  submissionId: string;
  currentDeadline: string;
  supplierName: string;
  onClose: () => void;
  onUpdate: () => void;
}

const DeadlineUpdateModal: React.FC<DeadlineUpdateModalProps> = ({
  submissionId,
  currentDeadline,
  supplierName,
  onClose,
  onUpdate
}) => {
  const [newDeadline, setNewDeadline] = useState('');
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Set current deadline as default
    setNewDeadline(new Date(currentDeadline).toISOString().split('T')[0]);
  }, [currentDeadline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newDeadline) {
      setError('Bitte wählen Sie eine neue Deadline');
      return;
    }

    const newDeadlineDate = new Date(newDeadline);
    const now = new Date();
    
    if (newDeadlineDate <= now) {
      setError('Die Deadline muss in der Zukunft liegen');
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      
      await updateDeadline(
        submissionId, 
        newDeadline, 
        reason || 'Deadline über Modal aktualisiert'
      );
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating deadline:', error);
      setError('Fehler beim Aktualisieren der Deadline');
    } finally {
      setUpdating(false);
    }
  };

  const getDaysExtension = () => {
    const current = new Date(currentDeadline);
    const newDate = new Date(newDeadline);
    const diffTime = newDate.getTime() - current.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const daysExtension = getDaysExtension();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* **HEADER** */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200/70">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center">
            <span className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mr-2.5">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </span>
            Deadline ändern
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* **CONTENT** */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* **SUPPLIER INFO** */}
            <div className="bg-blue-50 p-3 rounded-xl">
              <p className="text-sm font-medium text-blue-900">Lieferant</p>
              <p className="text-blue-700">{supplierName}</p>
            </div>

            {/* **CURRENT DEADLINE** */}
            <div className="bg-slate-50 p-3 rounded-xl">
              <p className="text-sm font-medium text-slate-900">Aktuelle Deadline</p>
              <p className="text-slate-600">{formatDate(currentDeadline)}</p>
            </div>

            {/* **NEW DEADLINE** */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Neue Deadline
              </label>
              <input
                type="date"
                value={newDeadline}
                onChange={(e) => setNewDeadline(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-400 transition-all"
                required
              />
            </div>

            {/* **EXTENSION INFO** */}
            {newDeadline && (
              <div className={`p-3 rounded-lg ${
                daysExtension > 0 
                  ? 'bg-green-50 border border-green-200' 
                  : daysExtension < 0 
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faInfoCircle} 
                    className={`mr-2 ${
                      daysExtension > 0 
                        ? 'text-green-600' 
                        : daysExtension < 0 
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  />
                  <p className={`text-sm font-medium ${
                    daysExtension > 0 
                      ? 'text-green-800' 
                      : daysExtension < 0 
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}>
                    {daysExtension > 0 
                      ? `Verlängerung um ${daysExtension} Tag${daysExtension !== 1 ? 'e' : ''}`
                      : daysExtension < 0 
                      ? `Verkürzung um ${Math.abs(daysExtension)} Tag${Math.abs(daysExtension) !== 1 ? 'e' : ''}`
                      : 'Keine Änderung'
                    }
                  </p>
                </div>
                <p className={`text-xs mt-1 ${
                  daysExtension > 0 
                    ? 'text-green-700' 
                    : daysExtension < 0 
                    ? 'text-red-700'
                    : 'text-yellow-700'
                }`}>
                  Neue Deadline: {formatDate(newDeadline)}
                </p>
              </div>
            )}

            {/* **REASON** */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Grund für Änderung (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="z.B. Lieferant benötigt mehr Zeit für Dokumentation..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-400 resize-none transition-all"
              />
            </div>

            {/* **ERROR MESSAGE** */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* **WARNING FOR SHORTENING** */}
            {daysExtension < 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Achtung</p>
                    <p className="text-sm text-yellow-700">
                      Die Deadline wird verkürzt. Der Lieferant sollte über diese Änderung informiert werden.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* **ACTIONS** */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={updating || !newDeadline || newDeadline === new Date(currentDeadline).toISOString().split('T')[0]}
              className="px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold transition-all"
            >
              {updating ? (
                <>
                  <FontAwesomeIcon icon={faClock} className="animate-spin mr-2" />
                  Aktualisiere...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="mr-2" />
                  Deadline ändern
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default DeadlineUpdateModal;